import { useEffect, useRef } from "react";
import { sendAgentEndOnUnload } from "./endSessionOnUnload";

/**
 * Ends the agent session when the user leaves the page (close tab/window,
 * refresh, external navigation). Uses a standard fetch POST with keepalive
 * (same JSON body as endAgentChat). pagehide is the primary trigger;
 * beforeunload is secondary. Does not fire on tab switch.
 */
export const useEndSessionOnPageUnload = (
  sessionId: string,
  version: string | undefined,
  enabled: boolean,
) => {
  const sessionIdRef = useRef(sessionId);
  const versionRef = useRef(version ?? "");
  const enabledRef = useRef(enabled);

  useEffect(() => {
    sessionIdRef.current = sessionId;
    versionRef.current = version ?? "";
    enabledRef.current = enabled;
  }, [sessionId, version, enabled]);

  useEffect(() => {
    const tryEndSession = (persisted?: boolean) => {
      if (persisted) return;
      if (!enabledRef.current || !sessionIdRef.current) return;
      sendAgentEndOnUnload(sessionIdRef.current, versionRef.current);
    };

    const onPageHide = (event: PageTransitionEvent) => {
      tryEndSession(event.persisted);
    };

    const onBeforeUnload = () => {
      tryEndSession(false);
    };

    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);
};
